import { Server as SocketServer, Socket } from 'socket.io'
import type { Server as HttpServer } from 'http'
import jwt from 'jsonwebtoken'
import { DEFAULT_TENANT_ID, entityRoom } from '@gzl10/nexus-sdk'
import { nexusEvents } from './emitter.js'
import { logger } from '@kernel/logger/index.js'
import { getAuthSecret } from '@kernel/crypto/secret-resolver.js'
import { env } from '@kernel/config/env.js'

let io: SocketServer | null = null
let jwtSecret: string | null = null
let currentOptions: SocketIOOptions | undefined = undefined

// Mapa de conexiones: userId -> Set<socketId>
const userSockets = new Map<string, Set<string>>()
// Mapa inverso: socketId -> userId
const socketUsers = new Map<string, string>()

interface JwtPayload {
  userId: string
  roleIds: string[]
}

export interface SocketIOOptions {
  /** JWT secret for token verification. If not provided, uses AUTH_SECRET env var */
  jwtSecret?: string
  /** CORS origin(s). If not provided, defaults to CORS_ORIGIN env var or '*' */
  corsOrigin?: string | string[] | boolean
  /**
   * Checks whether a role set can subscribe to real-time events for an entity.
   * Implementation must encapsulate subject lookup + CASL ability build.
   * Returns false to deny (socket will NOT join the room).
   */
  canSubscribeEntity?: (roleIds: string[], module: string, entity: string) => Promise<boolean>
}

/**
 * Initializes Socket.IO on the HTTP server
 * @param httpServer - HTTP server instance
 * @param options - Socket.IO initialization options (jwtSecret injected to avoid modules/ dependency)
 */
export function initSocketIO(httpServer: HttpServer, options?: SocketIOOptions): SocketServer {
  currentOptions = options

  // Store secret from options or resolved secret
  try {
    jwtSecret = options?.jwtSecret ?? getAuthSecret()
  } catch {
    jwtSecret = null
  }

  if (!jwtSecret) {
    logger.warn('Socket.IO authentication disabled: AUTH_SECRET not configured. All connections will be treated as guests.')
  }

  const corsOrigin = options?.corsOrigin ?? process.env['CORS_ORIGIN'] ?? '*'

  if (env.NODE_ENV === 'production' && !options?.corsOrigin && !process.env['CORS_ORIGIN']) {
    logger.warn('Socket.IO accepting connections from ANY origin in production. Configure CORS_ORIGIN env var.')
  }

  const pingInterval = parseInt(process.env['SOCKET_PING_INTERVAL_MS'] || '25000', 10)
  const pingTimeout = parseInt(process.env['SOCKET_PING_TIMEOUT_MS'] || '20000', 10)

  io = new SocketServer(httpServer, {
    cors: { origin: corsOrigin, methods: ['GET', 'POST'] },
    path: '/socket.io',
    maxHttpBufferSize: 1e6, // 1MB - match Express json body limit
    pingInterval,
    pingTimeout
  })

  logger.info({ cors: corsOrigin }, 'Socket.IO initialized')

  // Middleware de autenticación (opcional - permite guests)
  io.use((socket, next) => {
    const token = socket.handshake.auth?.['token'] || socket.handshake.query?.['token']

    if (token && typeof token === 'string' && jwtSecret) {
      try {
        const payload = jwt.verify(token, jwtSecret) as JwtPayload
        socket.data.userId = payload.userId
        socket.data.roleIds = payload.roleIds ?? []
        socket.data.authenticated = true
      } catch (err) {
        logger.debug({ err, socketId: socket.id }, 'Socket JWT verification failed')
        socket.data.authenticated = false
      }
    } else {
      socket.data.authenticated = false
    }

    // Tenant resolution: single-tenant uses DEFAULT_TENANT_ID.
    // NEX-6 will replace this with tenant resolution from JWT/header/subdomain.
    socket.data.tenantId = DEFAULT_TENANT_ID

    next()
  })

  io.engine.on('connection_error', (err: { code: number; message: string; context: unknown }) => {
    logger.warn({ code: err.code, message: err.message }, 'Socket.IO connection error')
  })

  io.on('connection', handleConnection)

  nexusEvents.emitEvent('socket.initialized')

  return io
}

function handleConnection(socket: Socket) {
  const { userId, roleIds, authenticated } = socket.data

  socket.on('error', (err) => {
    logger.error({ err, socketId: socket.id, userId: socket.data.userId }, 'Socket error')
  })

  // Registrar conexión
  if (authenticated && userId) {
    if (!userSockets.has(userId)) {
      userSockets.set(userId, new Set())
    }
    userSockets.get(userId)!.add(socket.id)
    socketUsers.set(socket.id, userId)

    // Warn if user has too many connections
    const userSocketList = userSockets.get(userId)!
    if (userSocketList.size > 3) {
      logger.warn({ userId, count: userSocketList.size }, 'User has multiple socket connections')
    }

    // Unir a rooms del usuario y de cada role
    socket.join(`user:${userId}`)
    for (const roleId of (roleIds as string[]) ?? []) {
      socket.join(`role:${roleId}`)
    }
    socket.join('authenticated')

    logger.debug({ userId, roleIds, socketId: socket.id }, 'User connected')
    nexusEvents.emitEvent('socket.user.connected', { userId, roleIds, socketId: socket.id })
  }

  // Unir a room global (todos, incluyendo guests)
  socket.join('all')

  // Entity real-time subscription protocol
  socket.on('entity:subscribe', async (entityKey: string) => {
    if (typeof entityKey !== 'string' || !entityKey) return
    const dotIndex = entityKey.indexOf('.')
    if (dotIndex <= 0) return
    const mod = entityKey.slice(0, dotIndex)
    const ent = entityKey.slice(dotIndex + 1)
    if (currentOptions?.canSubscribeEntity) {
      try {
        const allowed = await currentOptions.canSubscribeEntity(
          (socket.data.roleIds as string[]) ?? [],
          mod, ent
        )
        if (!allowed) {
          logger.warn({ entityKey, socketId: socket.id, authenticated: socket.data.authenticated }, 'entity:subscribe denied by CASL')
          return
        }
      } catch (err) {
        logger.error({ err, entityKey, socketId: socket.id }, 'entity:subscribe CASL check failed')
        return
      }
    }
    const room = entityRoom(socket.data.tenantId, mod, ent)
    socket.join(room)
    logger.debug({ entityKey, room, socketId: socket.id }, 'Socket subscribed to entity')
  })

  socket.on('entity:unsubscribe', (entityKey: string) => {
    if (!entityKey || typeof entityKey !== 'string') return
    const dotIndex = entityKey.indexOf('.')
    if (dotIndex === -1) return
    const mod = entityKey.slice(0, dotIndex)
    const ent = entityKey.slice(dotIndex + 1)
    const room = entityRoom(socket.data.tenantId, mod, ent)
    socket.leave(room)
    logger.debug({ entityKey, room, socketId: socket.id }, 'Socket unsubscribed from entity')
  })

  socket.on('disconnect', () => {
    if (userId) {
      userSockets.get(userId)?.delete(socket.id)
      if (userSockets.get(userId)?.size === 0) {
        userSockets.delete(userId)
      }
      socketUsers.delete(socket.id)

      logger.debug({ userId, socketId: socket.id }, 'User disconnected')
      nexusEvents.emitEvent('socket.user.disconnected', { userId, socketId: socket.id })
    }
  })
}

/**
 * Gets the Socket.IO instance
 * @throws Error if Socket.IO has not been initialized
 */
export function getIO(): SocketServer {
  if (!io) throw new Error('Socket.IO not initialized. Call initSocketIO first.')
  return io
}

/**
 * Checks whether Socket.IO is initialized
 */
export function isSocketIOInitialized(): boolean {
  return io !== null
}

/**
 * Gets IDs of connected users
 */
export function getConnectedUsers(): string[] {
  return Array.from(userSockets.keys())
}

/**
 * Checks whether a specific user is connected
 */
export function isUserConnected(userId: string): boolean {
  return userSockets.has(userId)
}

/**
 * Gets the number of sockets connected for a user
 */
export function getUserSocketCount(userId: string): number {
  return userSockets.get(userId)?.size ?? 0
}

// ============================================================================
// Room Management (CRUD)
// ============================================================================

/**
 * Joins a user to a custom room (all their sockets)
 */
export function joinUserToRoom(userId: string, room: string): boolean {
  if (!io) return false
  const sockets = userSockets.get(userId)
  if (!sockets || sockets.size === 0) return false

  for (const socketId of sockets) {
    const socket = io.sockets.sockets.get(socketId)
    socket?.join(room)
  }
  logger.debug({ userId, room }, 'User joined room')
  return true
}

/**
 * Removes a user from a custom room (all their sockets)
 */
export function removeUserFromRoom(userId: string, room: string): boolean {
  if (!io) return false
  const sockets = userSockets.get(userId)
  if (!sockets || sockets.size === 0) return false

  for (const socketId of sockets) {
    const socket = io.sockets.sockets.get(socketId)
    socket?.leave(room)
  }
  logger.debug({ userId, room }, 'User left room')
  return true
}

/**
 * Joins a specific socket to a room
 */
export function joinSocketToRoom(socketId: string, room: string): boolean {
  if (!io) return false
  const socket = io.sockets.sockets.get(socketId)
  if (!socket) return false

  socket.join(room)
  return true
}

/**
 * Removes a specific socket from a room
 */
export function removeSocketFromRoom(socketId: string, room: string): boolean {
  if (!io) return false
  const socket = io.sockets.sockets.get(socketId)
  if (!socket) return false

  socket.leave(room)
  return true
}

/**
 * Gets all user IDs in a room
 */
export async function getRoomMembers(room: string): Promise<string[]> {
  if (!io) return []

  const sockets = await io.in(room).fetchSockets()
  const userIds = new Set<string>()

  for (const socket of sockets) {
    const userId = (socket as any).data?.userId
    if (userId) userIds.add(userId)
  }

  return Array.from(userIds)
}

/**
 * Gets the number of sockets in a room
 */
export async function getRoomSize(room: string): Promise<number> {
  if (!io) return 0
  const sockets = await io.in(room).fetchSockets()
  return sockets.length
}

/**
 * Gets all rooms a user is in (excluding system rooms)
 */
export function getUserRooms(userId: string): string[] {
  if (!io) return []

  const sockets = userSockets.get(userId)
  if (!sockets || sockets.size === 0) return []

  const rooms = new Set<string>()
  const systemRooms = ['all', 'authenticated', `user:${userId}`]

  for (const socketId of sockets) {
    const socket = io.sockets.sockets.get(socketId)
    if (socket) {
      for (const room of socket.rooms) {
        // Excluir el socketId (siempre está en su propia "room") y rooms del sistema
        if (room !== socketId && !systemRooms.includes(room) && !room.startsWith('role:')) {
          rooms.add(room)
        }
      }
    }
  }

  return Array.from(rooms)
}

/**
 * Checks if a room has any members
 */
export async function roomExists(room: string): Promise<boolean> {
  if (!io) return false
  const size = await getRoomSize(room)
  return size > 0
}

/**
 * Emits an event to a specific room
 */
export function emitToRoom(room: string, event: string, data: unknown): boolean {
  if (!io) return false
  const sockets = io.sockets.adapter.rooms.get(room)
  logger.debug({ room, event, socketsInRoom: sockets?.size ?? 0 }, '[DEBUG-RT] emitToRoom')
  io.to(room).emit(event, data)
  return true
}

// ============================================================================
// Emission Helpers
// ============================================================================

/**
 * Emits an event to a specific user (all their sockets)
 */
export function emitToUser(userId: string, event: string, data: unknown): boolean {
  if (!io) return false
  io.to(`user:${userId}`).emit(event, data)
  return true
}

/**
 * Emits an event to all users with a specific role
 */
export function emitToRole(roleId: string, event: string, data: unknown): boolean {
  if (!io) return false
  io.to(`role:${roleId}`).emit(event, data)
  return true
}

/**
 * Emits an event to all connected sockets
 */
export function emitToAll(event: string, data: unknown): boolean {
  if (!io) return false
  io.to('all').emit(event, data)
  return true
}

/**
 * Emits an event to all authenticated users
 */
export function emitToAuthenticated(event: string, data: unknown): boolean {
  if (!io) return false
  io.to('authenticated').emit(event, data)
  return true
}

/**
 * Broadcasts to a room except specific user(s)
 */
export function broadcastToRoom(
  room: string,
  exceptUserIds: string | string[],
  event: string,
  data: unknown
): boolean {
  if (!io) return false

  const excludeIds = Array.isArray(exceptUserIds) ? exceptUserIds : [exceptUserIds]
  const excludeRooms = excludeIds.map(id => `user:${id}`)

  io.to(room).except(excludeRooms).emit(event, data)
  return true
}

/**
 * Closes Socket.IO (for test cleanup or shutdown)
 */
export function closeSocketIO(): void {
  if (io) {
    io.close()
    io = null
    jwtSecret = null
    currentOptions = undefined
    userSockets.clear()
    socketUsers.clear()
    logger.debug('Socket.IO closed')
  }
}

/**
 * Executes callback when Socket.IO is ready.
 * If already initialized, runs immediately. Otherwise waits for 'socket.initialized' event.
 */
export function onSocketReady(callback: () => void): void {
  if (io) {
    callback()
  } else {
    nexusEvents.onceEvent('socket.initialized', callback)
  }
}
